import { createReducer, on } from '@ngrx/store';
import {
  DonationFlowUiActions,
  DonationFlowStripeActions,
  DonationFlowHelpersActions,
  hideFeeReview,
  showFeeReview,
} from './actions';
import { DonationFlowState, DonationType, FeeCalculationStatus } from '../../types/donation-flow.models';
import {
  mapCustomerToStore,
  mapInvoiceToStore,
  mapPriceToStore,
  mapSetupIntentToStore,
  mapSubscriptionToStore,
} from '../../types/stripe-adapters';
import { RecurringPaymentActions } from '../payment/actions';

export const initialState: DonationFlowState = {
  isLoading: false,
  step: 1,
  flowMode: null,
  donationType: null,
  metadata: null,
  priceDraft: null,
  price: null,
  customer: null,
  invoice: null,
  feesCovered: true,
  cardFeeType: null,
  feeCalculationStatus: 'not-calculated' as FeeCalculationStatus,
  isFirstChargeDeferred: false,
  firstPaymentDate: null,
  setupIntent: null,
  paymentMethodId: null,
  feeReview: { visible: false },
  subscription: null,
  error: null,
};

export const donationFlowReducer = createReducer(
  initialState,

  // UI
  on(DonationFlowUiActions.reset, () => initialState),
  on(DonationFlowUiActions.setStep, (s, { step }) => ({ ...s, step })),
  on(DonationFlowUiActions.setDonationType, (s, { donationType }) => ({
    ...s,
    donationType,
  })),
  /**
   * Central cleanup when the donor switches OneTime ↔ Recurring.
   * We keep common data the user is likely to reuse (campaign, priceDraft,
   * donor customer/metadata, feesCovered) and wipe every value tied to the
   * previous flow's Stripe artifacts, fees, invoice/subscription and errors.
   * Payment slice cleanup is handled in a payment effect (viewExited + reset).
   */
  on(DonationFlowUiActions.changeDonationType, (s, { donationType }) => {
    if (s.donationType === donationType) {
      return s;
    }
    return {
      ...s,
      donationType,
      // metadata references the old donation type; clear it (donor form data
      // is rebuilt from `customer` so we don't lose donor input).
      metadata: null,
      setupIntent: null,
      paymentMethodId: null,
      cardFeeType: null,
      feeCalculationStatus: 'not-calculated' as FeeCalculationStatus,
      feeReview: { visible: false },
      invoice: null,
      subscription: null,
      isFirstChargeDeferred: false,
      firstPaymentDate: null,
      isLoading: false,
      error: null,
    };
  }),
  on(DonationFlowUiActions.setFlowMode, (s, { flowMode }) => ({
    ...s,
    flowMode,
  })),
  on(DonationFlowUiActions.setDonationMetadata, (s, { metadata }) => ({
    ...s,
    metadata,
  })),
  on(DonationFlowUiActions.setPriceDraft, (s, { draft }) => ({
    ...s,
    priceDraft: draft,
  })),

  // Hydrate tras confirmación en cliente (Payment Element) – ahora con SetupIntent
  on(
    DonationFlowUiActions.hydrateSetupIntentAfterClientConfirm,
    (s, { setupIntent }) => {
      const si = mapSetupIntentToStore(setupIntent);
      return {
        ...s,
        setupIntent: si,
        // si el adapter ya mapea payment_method, perfecto; si no, ajusta:
        paymentMethodId: si.payment_method ?? s.paymentMethodId,
      };
    }
  ),

  on(DonationFlowUiActions.toggleCoverFees, (s, { coverFees }) => ({
    ...s,
    feesCovered: coverFees,
  })),

  on(DonationFlowUiActions.setCardFeeType, (s, { cardFeeType }) => ({
    ...s,
    cardFeeType,
  })),

  on(DonationFlowUiActions.setFeeCalculationStatus, (s, { status }) => ({
    ...s,
    feeCalculationStatus: status,
  })),

  on(DonationFlowUiActions.resetCardBrandState, (s) => ({
    ...s,
    cardFeeType: null,
    paymentMethodId: null,
    setupIntent: null,
    feeReview: { visible: false },
    feeCalculationStatus: 'not-calculated' as FeeCalculationStatus,
  })),

  on(RecurringPaymentActions.confirmRequested, (s) => ({
    ...s,
    isLoading: true,
    error: null,
  })),
  on(RecurringPaymentActions.confirmFailed, (s, { error }) => ({
    ...s,
    isLoading: false,
    error: error,
  })),

  on(DonationFlowUiActions.setDeferFirstCharge, (s, { isFirstChargeDeferred: isFirstChargeDeferred }) => ({
    ...s,
    isFirstChargeDeferred,
  })),

  on(DonationFlowUiActions.setFirstPaymentDate, (s, { firstPaymentDate }) => ({
  ...s,
  firstPaymentDate,
})),

  // Start -> loading
  on(
    DonationFlowStripeActions.sendCustomer,
    DonationFlowStripeActions.sendPrice,
    DonationFlowStripeActions.sendSetupIntent,
    DonationFlowStripeActions.sendSubscription,
    DonationFlowStripeActions.sendInvoice,
    DonationFlowHelpersActions.confirmSubscriptionFirstInvoicePayment,
    DonationFlowHelpersActions.retrieveSubscription,
    (s) => ({ ...s, isLoading: true, error: null })
  ),

  // Customer
  on(DonationFlowStripeActions.sendCustomerSuccess, (s, { response }) => ({
    ...s,
    isLoading: false,
    customer: mapCustomerToStore(response),
  })),
  on(DonationFlowStripeActions.sendCustomerFailure, (s, { error }) => ({
    ...s,
    isLoading: false,
    error,
  })),

  // Price
  on(DonationFlowStripeActions.sendPriceSuccess, (s, { response }) => ({
    ...s,
    isLoading: false,
    price: mapPriceToStore(response),
  })),
  on(DonationFlowStripeActions.sendPriceFailure, (s, { error }) => ({
    ...s,
    isLoading: false,
    error,
  })),

  // ===== SetupIntent (ambos flujos) =====
  on(DonationFlowStripeActions.sendSetupIntentSuccess, (s, { response }) => {
    const si = mapSetupIntentToStore(response);
    return {
      ...s,
      isLoading: false,
      setupIntent: si,
      paymentMethodId: si.payment_method ?? s.paymentMethodId,
    };
  }),
  on(DonationFlowStripeActions.sendSetupIntentFailure, (s, { error }) => ({
    ...s,
    isLoading: false,
    error,
  })),

  // ===== Subscription (recurring) =====
  on(DonationFlowStripeActions.sendSubscriptionSuccess, (s, { response }) => ({
    ...s,
    isLoading: false,
    subscription: mapSubscriptionToStore(response),
  })),
  on(DonationFlowStripeActions.sendSubscriptionFailure, (s, { error }) => ({
    ...s,
    isLoading: false,
    error,
  })),

  on(
    showFeeReview,
    (s, { feeType, currency, baseMinor, oldFeeMinor, newFeeMinor }) => ({
      ...s,
      feeReview: {
        visible: true,
        feeType,
        currency,
        baseMinor,
        oldFeeMinor,
        newFeeMinor,
      },
    })
  ),

  on(hideFeeReview, (s) => ({
    ...s,
    feeReview: { visible: false },
  })),

  // ===== Invoice (one-time) =====
  on(DonationFlowStripeActions.sendInvoiceSuccess, (s, { response }) => ({
    ...s,
    isLoading: false,
    invoice: mapInvoiceToStore(response),
  })),
  on(DonationFlowStripeActions.sendInvoiceFailure, (s, { error }) => ({
    ...s,
    isLoading: false,
    error,
  })),

  // Helpers
  on(
    DonationFlowHelpersActions.setPaymentMethodIdFromConfirm,
    (s, { paymentMethodId, setupIntentId, status }) => ({
      ...s,
      paymentMethodId,
      setupIntent: s.setupIntent
        ? {
            ...s.setupIntent,
            id: setupIntentId,
            ...(typeof status === 'string' ? { status } : {}),
          }
        : {
            id: setupIntentId,
            ...(typeof status === 'string' ? { status } : {}),
          },
    })
  ),

  // Confirmación del pago del primer invoice de la suscripción (flujo recurring)
  on(DonationFlowHelpersActions.confirmInvoicePaymentSuccess, (s) => ({
    ...s,
    // si quieres guardar el PI del invoice, puedes añadir un campo opcional
    // isLoading: false,
  })),
  on(
    DonationFlowHelpersActions.confirmInvoicePaymentFailure,
    (s, { error }) => ({
      ...s,
      isLoading: false,
      error,
    })
  ),

  on(
    DonationFlowHelpersActions.retrieveSubscriptionSuccess,
    (s, { response }) => ({
      ...s,
      isLoading: false,
      subscription: mapSubscriptionToStore(response),
    })
  ),
  on(
    DonationFlowHelpersActions.retrieveSubscriptionFailure,
    (s, { error }) => ({
      ...s,
      isLoading: false,
      error,
    })
  ),

  on(
    DonationFlowHelpersActions.finalizeRecurringSuccess,
    (s, { subscriptionId }) => ({
      ...s,
    isLoading: false,
    step: 4,
    })
  )
);
